import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DocumentUploadSection } from "@/components/workflow/common/DocumentUploadSection";
import type { DocumentMetadata } from "@/contexts/WizardContext";
import { BadgeDollarSign, HelpCircle } from "lucide-react";
import { SectionCard } from "@/components/workflow/common/SectionCard";
import { CurrencySelect } from "@/components/workflow/common/CurrencySelect";

function computeRevenueRange(netTurnover: number): string {
  if (netTurnover <= 0) return "";
  if (netTurnover <= 50000) return "0-50k";
  if (netTurnover <= 100000) return "50k-100k";
  if (netTurnover <= 500000) return "100k-500k";
  if (netTurnover <= 1000000) return "500k-1m";
  if (netTurnover <= 5000000) return "1m-5m";
  if (netTurnover <= 10000000) return "5m-10m";
  return "10m+";
}

const rangeLabels: Record<string, string> = {
  "0-50k": "0 - 50,000",
  "50k-100k": "50,000 - 100,000",
  "100k-500k": "100,000 - 500,000",
  "500k-1m": "500,000 - 1,000,000",
  "1m-5m": "1,000,000 - 5,000,000",
  "5m-10m": "5,000,000 - 10,000,000",
  "10m+": "10,000,000+",
};

interface FinancialInfoFormProps {
  generalInfo: any;
  updateGeneralInfo: (updates: any) => void;
  isLocked?: boolean;
}

const fieldLabelClassName = "text-sm font-medium leading-5 text-slate-700";
const fieldInputClassName = "h-11 rounded-2xl border-slate-200 bg-slate-50 px-3 text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-slate-300";
const fieldSelectClassName = "h-11 rounded-2xl border-slate-200 bg-slate-50 px-3 text-base text-slate-900 shadow-none data-[placeholder]:text-slate-400 focus:ring-0";

export function FinancialInfoForm({ generalInfo, updateGeneralInfo, isLocked }: FinancialInfoFormProps) {
  const header = (
    <div className="flex min-w-0 items-center gap-2">
      <BadgeDollarSign className="h-5 w-5 flex-shrink-0 text-[#00444C]" />
      <h3 className="text-lg font-bold leading-7 text-slate-900">{"Financial Information"}</h3>
      <HelpCircle className="h-4 w-4 flex-shrink-0 text-slate-600" />
    </div>
  );

  return (
    <SectionCard header={header}>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {"Reporting Currency"} <span className="text-red-500">*</span>
          </Label>
          <CurrencySelect
            value={generalInfo.reportingCurrency}
            onValueChange={(v) => updateGeneralInfo({ reportingCurrency: v })}
            disabled={isLocked}
            triggerClassName={fieldSelectClassName}
            placeholder="Select currency"
            data-testid="select-currency"
          />
        </div>
        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {`Net Turnover (${generalInfo.reportingCurrency})`} <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            placeholder={"e.g. 250000"}
            value={generalInfo.netTurnover}
            onChange={(e) => {
              const value = e.target.value;
              const turnover = parseFloat(value) || 0;
              updateGeneralInfo({ netTurnover: value, annualRevenueRange: computeRevenueRange(turnover) });
            }}
            data-testid="input-net-turnover"
            disabled={isLocked}
            className={fieldInputClassName}
          />
        </div>
        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {`Annual Revenue Range (${generalInfo.reportingCurrency})`}
          </Label>
          <Input
            readOnly
            data-testid="display-revenue-range"
            value={(() => {
              const range = computeRevenueRange(parseFloat(generalInfo.netTurnover || "0"));
              return range ? rangeLabels[range] : "Not entered";
            })()}
            className={`${fieldInputClassName} cursor-default text-slate-500`}
          />
        </div>
        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {`Balance Sheet Total (${generalInfo.reportingCurrency})`} <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            placeholder={"e.g. 500000"}
            value={generalInfo.balanceSheetTotal}
            onChange={(e) => updateGeneralInfo({ balanceSheetTotal: e.target.value })}
            data-testid="input-balance-sheet"
            disabled={isLocked}
            className={fieldInputClassName}
          />
        </div>
      </div>
      <div className="pt-4">
        <DocumentUploadSection
          fieldIdentifier="general-financial-statements"
          documents={generalInfo.financialStatementsDocuments || []}
          onDocumentsChange={(docs: DocumentMetadata[]) => updateGeneralInfo({ financialStatementsDocuments: docs })}
          currentTab="general"
          label={"Financial Statements (optional)"}
          description="Upload financial statements if available (optional). This helps support the figures entered above."
          accept=".pdf,.xlsx,.xls,.csv"
          maxFiles={5}
          className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
          itemClassName="rounded-2xl border border-slate-200 bg-white p-3"
          actionButtonClassName="h-9 rounded-2xl border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-none hover:bg-slate-100"
        />
      </div>
    </SectionCard>
  );
}
